import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { usePermissions } from "../../../PermissionsProvider";
import { DatasetService } from "../../../services/DatasetService";
import { CompositionService } from "../../../services/CompositionService";
import CompositionBaseType from "./Steps/CompositionBaseType";
import CompositionDetails from "./Steps/CompositionDetails";
import CompositionBuild from "./Steps/CompositionBuild";
import CompositionReview from "./Steps/CompositionReview";
import { SelectedDataset, Dataset } from "./CompositionDetail";
import { Button } from "../../ui/button";
import { Card, CardContent } from "../../ui/card";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../ui/dialog";
import { 
  CheckIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  Loader2Icon,
  FileTextIcon
} from "lucide-react";
import { cn } from "../../../lib/utils";
import { notification } from "antd";

interface CompositionData {
  name: string;
  description: string;
  baseType: "intent_group" | "application_intent" | undefined;
  intentGroupId: number | undefined;
  applicationId: number | undefined;
  intentId: number | undefined;
  datasets: {
    id: number;
    percentage: number;
    sampleType: "request" | "session";
    samplePercentage: number;
  }[];
}

interface CompositionMember {
  dataset_id: number;
  dataset_name: string;
  dataset_description: string;
  sampling_type: string;
  sampling_weight: number;
  origin_dataset_id: number;
  request_count: number;
}

interface ExistingComposition {
  id: number;
  name: string;
  description: string;
  major_version: number;
  minor_version: number;
  parent_composition_id?: number;
  meta: any;
  application_id?: number;
  intent_id?: number;
  intent_group_id?: number;
  date_created: number;
  base_type: string;
  members: CompositionMember[];
  member_count: number;
}

interface StepItem {
  key: string;
  title: string;
  component: React.ReactNode;
}

const CompositionEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { apiKey } = usePermissions();
  const [currentStep, setCurrentStep] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [datasetsLoading, setDatasetsLoading] = useState<boolean>(false);
  const [availableDatasets, setAvailableDatasets] = useState<Dataset[]>([]);
  const [selectedDatasets, setSelectedDatasets] = useState<SelectedDataset[]>([]);
  const [totalPercentage, setTotalPercentage] = useState<number>(0);
  const [existingComposition, setExistingComposition] = useState<ExistingComposition | null>(null);
  const [isLoadingComposition, setIsLoadingComposition] = useState<boolean>(true);

  // Configuration modal state
  const [isConfigModalVisible, setIsConfigModalVisible] = useState(false);
  const [configDataset, setConfigDataset] = useState<SelectedDataset | null>(null);
  const [configSampleType, setConfigSampleType] = useState<"request" | "session">("request");
  const [configSamplePercentage, setConfigSamplePercentage] = useState<number>(100);

  const [compositionData, setCompositionData] = useState<CompositionData>({
    name: "",
    description: "",
    baseType: undefined,
    intentGroupId: undefined,
    applicationId: undefined,
    intentId: undefined,
    datasets: []
  });

  // Load existing composition data
  useEffect(() => {
    if (id) {
      setIsLoadingComposition(true);
      CompositionService.getComposition(id, setIsLoadingComposition, apiKey).subscribe(
        (data: ExistingComposition) => {
          if (data) {
            setExistingComposition(data);
            // Convert existing composition to form data
            setCompositionData({
              name: data.name,
              description: data.description,
              baseType: data.base_type as "intent_group" | "application_intent",
              intentGroupId: data.intent_group_id,
              applicationId: data.application_id,
              intentId: data.intent_id,
              datasets: data.members.map(member => ({
                id: member.origin_dataset_id,
                percentage: Math.round((member.request_count / data.members.reduce((sum, m) => sum + m.request_count, 0)) * 100),
                sampleType: member.sampling_type.toLowerCase() as "request" | "session",
                samplePercentage: member.sampling_weight,
              }))
            });
          } else {
            notification.error({
              message: "Error",
              description: "Failed to load composition data",
            });
            navigate("/build/compositions");
          }
        },
        (error: any) => {
          console.error("Error fetching composition:", error);
          notification.error({
            message: "Error",
            description: `Failed to load composition: ${error}`,
          });
          navigate("/build/compositions");
        }
      );
    }
  }, [id, apiKey, navigate]);

  // Load selected datasets when composition data is loaded
  useEffect(() => {
    if (existingComposition && availableDatasets.length > 0) {
      const loadedSelectedDatasets: SelectedDataset[] = [];
      
      existingComposition.members.forEach(member => {
        const dataset = availableDatasets.find(d => d.id === member.origin_dataset_id);
        if (dataset) {
          loadedSelectedDatasets.push({
            dataset,
            percentage: 0, // Will be recalculated below
            sampleType: member.sampling_type.toLowerCase() as "request" | "session",
            samplePercentage: member.sampling_weight,
          });
        }
      });
      
      // Recalculate percentages properly using the same logic as when adding datasets
      const recalculatedDatasets = recalculatePercentagesBasedOnSampling(loadedSelectedDatasets);
      setSelectedDatasets(recalculatedDatasets);
      updateTotalPercentage(recalculatedDatasets);
    }
  }, [existingComposition, availableDatasets]);

  // Fetch available datasets
  useEffect(() => {
    if (compositionData.baseType) {
      fetchDatasets();
    }
  }, [apiKey, compositionData.baseType, compositionData.intentGroupId, compositionData.applicationId, compositionData.intentId]);

  const fetchDatasets = async () => {
    if (compositionData.baseType === "intent_group" && compositionData.intentGroupId) {
      // Fetch datasets filtered by intent group
      DatasetService.getDatasetsByIntentGroup(
        compositionData.intentGroupId,
        0,
        100,
        setDatasetsLoading,
        apiKey
      ).subscribe(
        (response: any) => {
          setAvailableDatasets(response.items || response || []);
        },
        (error: any) => {
          console.error("Error fetching datasets by intent group:", error);
          notification.error({
            message: "Error",
            description: `Failed to fetch datasets: ${error}`,
          });
        }
      );
    } else if (compositionData.baseType === "application_intent" && 
               compositionData.applicationId && 
               compositionData.intentId) {
      // Fetch datasets filtered by application/intent
      DatasetService.getDatasetsByIntent(
        compositionData.applicationId,
        compositionData.intentId,
        0,
        100,
        setDatasetsLoading,
        apiKey
      ).subscribe(
        (response: any) => {
          setAvailableDatasets(response.items || response || []);
        },
        (error: any) => {
          console.error("Error fetching datasets by application/intent:", error);
          notification.error({
            message: "Error",
            description: `Failed to fetch datasets: ${error}`,
          });
        }
      );
    } else {
      // Fetch all datasets if no base type is selected or if selection is incomplete
      DatasetService.getDatasets(0, 100, setDatasetsLoading, apiKey).subscribe(
        (response: any) => {
          setAvailableDatasets(response.items || []);
        },
        (error: any) => {
          console.error("Error fetching datasets:", error);
          notification.error({
            message: "Error",
            description: `Failed to fetch datasets: ${error}`,
          });
        }
      );
    }
  };

  // Calculate percentages based on sampled request counts
  const recalculatePercentagesBasedOnSampling = (datasets: SelectedDataset[]): SelectedDataset[] => {
    if (datasets.length === 0) return [];
    
    // Calculate sampled request counts
    const datasetsWithSampledCounts = datasets.map(item => ({
      ...item,
      sampledCount: Math.round((item.dataset.request_count * item.samplePercentage) / 100)
    }));
    
    // Calculate total sampled requests
    const totalSampledRequests = datasetsWithSampledCounts.reduce(
      (sum, item) => sum + item.sampledCount, 0
    );
    
    if (totalSampledRequests === 0) {
      return datasets.map(item => ({ ...item, percentage: 0 }));
    }
    
    // Calculate new percentages based on sampled counts
    const updatedDatasets = datasetsWithSampledCounts.map(item => ({
      ...item,
      percentage: Math.round((item.sampledCount / totalSampledRequests) * 100)
    }));
    
    // Ensure percentages add up to exactly 100% by adjusting the largest dataset
    const currentTotal = updatedDatasets.reduce((sum, item) => sum + item.percentage, 0);
    if (currentTotal !== 100 && updatedDatasets.length > 0) {
      // Find the dataset with the most sampled requests and adjust it
      const sortedByCount = [...updatedDatasets].sort((a, b) => b.sampledCount - a.sampledCount);
      sortedByCount[0].percentage += (100 - currentTotal);
    }
    
    // Remove the sampledCount property as it's not part of the interface
    return updatedDatasets.map(({ sampledCount, ...rest }) => rest);
  };

  const updateTotalPercentage = (datasets: SelectedDataset[]) => {
    const total = datasets.reduce((sum, item) => sum + item.percentage, 0);
    setTotalPercentage(total);
  };

  // Update composition data when form values change
  const handleFormValuesChange = (changedValues: any) => {
    setCompositionData((prevData: CompositionData) => ({
      ...prevData,
      ...changedValues,
    }));
  };

  // Add dataset to composition
  const handleAddDataset = (dataset: Dataset) => {
    try {
      // Check if dataset is already in composition
      if (selectedDatasets.some((item) => item.dataset.id === dataset.id)) {
        notification.warning({
          message: "Dataset already added",
          description: "This dataset is already part of your composition.",
        });
        return;
      }

      // Add new dataset with default sampling settings
      const newSelectedDatasets: SelectedDataset[] = [...selectedDatasets, { 
        dataset, 
        percentage: 0, 
        sampleType: "request" as const, 
        samplePercentage: 100 
      }];
      
      // Recalculate percentages based on sampled request counts
      const updatedDatasets = recalculatePercentagesBasedOnSampling(newSelectedDatasets);
      
      setSelectedDatasets(updatedDatasets);
      updateTotalPercentage(updatedDatasets);
      
      // Update composition data
      setCompositionData((prevData: CompositionData) => {
        return {
          ...prevData,
          datasets: updatedDatasets.map(item => ({
            id: item.dataset.id,
            percentage: item.percentage,
            sampleType: item.sampleType,
            samplePercentage: item.samplePercentage,
          })),
        };
      });
    } catch (error) {
      console.error("Error in handleAddDataset:", error);
    }
  };

  // Remove dataset from composition
  const handleRemoveDataset = (datasetId: number) => {
    const updatedDatasets = selectedDatasets.filter((item) => item.dataset.id !== datasetId);
    const recalculatedDatasets = recalculatePercentagesBasedOnSampling(updatedDatasets);
    
    setSelectedDatasets(recalculatedDatasets);
    updateTotalPercentage(recalculatedDatasets);
    
    // Update composition data
    setCompositionData((prevData: CompositionData) => ({
      ...prevData,
      datasets: recalculatedDatasets.map(item => ({
        id: item.dataset.id,
        percentage: item.percentage,
        sampleType: item.sampleType,
        samplePercentage: item.samplePercentage,
      })),
    }));
  };

  // Configure dataset sampling
  const handleConfigureDataset = (dataset: SelectedDataset) => {
    setConfigDataset(dataset);
    setConfigSampleType(dataset.sampleType);
    setConfigSamplePercentage(dataset.samplePercentage);
    setIsConfigModalVisible(true);
  };

  const handleConfigModalOk = () => {
    if (!configDataset) return;

    const updatedDatasets = selectedDatasets.map((item) => {
      if (item.dataset.id === configDataset.dataset.id) {
        return {
          ...item,
          sampleType: configSampleType,
          samplePercentage: configSamplePercentage,
        };
      }
      return item;
    });

    const recalculatedDatasets = recalculatePercentagesBasedOnSampling(updatedDatasets);
    setSelectedDatasets(recalculatedDatasets);
    updateTotalPercentage(recalculatedDatasets);
    
    // Update composition data
    setCompositionData((prevData: CompositionData) => ({
      ...prevData,
      datasets: recalculatedDatasets.map(item => ({
        id: item.dataset.id,
        percentage: item.percentage,
        sampleType: item.sampleType,
        samplePercentage: item.samplePercentage,
      })),
    }));

    setIsConfigModalVisible(false);
    setConfigDataset(null);
  };

  // Steps components
  const steps: StepItem[] = [
    {
      key: "base-type",
      title: "Base Type",
      component: (
        <CompositionBaseType
          baseType={compositionData.baseType}
          intentGroupId={compositionData.intentGroupId}
          applicationId={compositionData.applicationId}
          intentId={compositionData.intentId}
          onChange={(data) => {
            // Clear selected datasets when base type changes
            if (data.baseType !== compositionData.baseType) {
              setSelectedDatasets([]);
              updateTotalPercentage([]);
            }
            
            setCompositionData((prevData) => ({
              ...prevData,
              baseType: data.baseType || prevData.baseType,
              intentGroupId: data.intentGroupId,
              applicationId: data.applicationId,
              intentId: data.intentId,
              // Clear datasets if base type changes
              datasets: data.baseType !== prevData.baseType ? [] : prevData.datasets
            }));
          }}
        />
      ),
    },
    {
      key: "details",
      title: "Details",
      component: (
        <CompositionDetails
          name={compositionData.name}
          description={compositionData.description}
          onChange={handleFormValuesChange}
        />
      ),
    },
    {
      key: "build",
      title: "Build",
      component: (
        <CompositionBuild
          availableDatasets={availableDatasets}
          selectedDatasets={selectedDatasets}
          datasetsLoading={datasetsLoading}
          onAddDataset={handleAddDataset}
          onRemoveDataset={handleRemoveDataset}
          onConfigureDataset={handleConfigureDataset}
        />
      ),
    },
    {
      key: "review",
      title: "Review",
      component: (
        <CompositionReview
          name={compositionData.name}
          description={compositionData.description}
          selectedDatasets={selectedDatasets}
          onConfigureDataset={handleConfigureDataset}
          onEditStep={() => setCurrentStep(2)} // Go back to Build step
          isEdit={true}
          originalComposition={existingComposition}
        />
      ),
    },
  ];

  const canProceed = () => {
    if (currentStep === 0) {
      // Base Type step validation
      if (compositionData.baseType === "intent_group") {
        return !!compositionData.intentGroupId;
      } else if (compositionData.baseType === "application_intent") {
        return !!compositionData.applicationId && !!compositionData.intentId;
      }
      return !!compositionData.baseType;
    }
    if (currentStep === 1) {
      return !!compositionData.name && !!compositionData.description && compositionData.description.trim().length > 0;
    }
    if (currentStep === 2) {
      // Allow small rounding errors in percentage calculation (within 1%)
      return selectedDatasets.length > 0 && Math.abs(totalPercentage - 100) <= 1;
    }
    return true;
  };

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const updateComposition = async () => {
    if (!id) return;
    
    setLoading(true);
    
    const payload = {
      name: compositionData.name,
      description: compositionData.description,
      base_type: compositionData.baseType,
      intent_group_id: compositionData.baseType === "intent_group" ? compositionData.intentGroupId : undefined,
      application_id: compositionData.baseType === "application_intent" ? compositionData.applicationId : undefined,
      intent_id: compositionData.baseType === "application_intent" ? compositionData.intentId : undefined,
      datasets: selectedDatasets.map(item => ({
        dataset_id: item.dataset.id,
        percentage: item.percentage,
        sample_type: item.sampleType,
        sample_percentage: item.samplePercentage
      }))
    };
    
    CompositionService.updateComposition(id, payload, setLoading, apiKey).subscribe(
      (response: any) => {
        notification.success({
          message: "Composition updated successfully",
          description: "A new version of your composition has been created successfully",
        });
        // Navigate to the new composition version
        if (response && response.id) {
          navigate(`/build/compositions/${response.id}`);
        } else {
          navigate("/build/compositions");
        }
      },
      (error: any) => {
        console.error("Error updating composition:", error);
        notification.error({
          message: "Error",
          description: `Failed to update composition: ${error}`,
        });
        setLoading(false);
      }
    );
  };

  const completedSteps = steps.slice(0, currentStep).filter((_, index) => {
    // Check if each previous step would be valid
    if (index === 0) {
      if (compositionData.baseType === "intent_group") {
        return !!compositionData.intentGroupId;
      } else if (compositionData.baseType === "application_intent") {
        return !!compositionData.applicationId && !!compositionData.intentId;
      }
      return !!compositionData.baseType;
    }
    if (index === 1) {
      return !!compositionData.name && !!compositionData.description && compositionData.description.trim().length > 0;
    }
    if (index === 2) {
      return selectedDatasets.length > 0 && Math.abs(totalPercentage - 100) <= 1;
    }
    return true;
  }).length;

  if (isLoadingComposition) {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-4xl mx-auto px-8 py-8 space-y-8">
          {/* Header Card Loading */}
          <Card className="border-border">
            <CardContent className="p-8">
              <div className="h-4 bg-muted rounded w-32 animate-pulse mb-6" />
              <div className="space-y-2">
                <div className="h-10 bg-muted rounded w-64 animate-pulse" />
                <div className="h-5 bg-muted rounded w-96 animate-pulse" />
              </div>
            </CardContent>
          </Card>

          {/* Progress Card Loading */}
          <Card className="border-border">
            <CardContent className="p-8">
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <div className="space-y-2">
                    <div className="h-6 bg-muted rounded w-32 animate-pulse" />
                    <div className="h-4 bg-muted rounded w-24 animate-pulse" />
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="h-4 bg-muted rounded w-20 animate-pulse" />
                    <div className="flex items-center gap-1">
                      {[...Array(4)].map((_, i) => (
                        <div key={i} className="w-2 h-2 bg-muted rounded-full animate-pulse" />
                      ))}
                    </div>
                  </div>
                </div>
                <div className="flex space-x-8">
                  {[...Array(4)].map((_, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <div className="w-6 h-6 bg-muted rounded-full animate-pulse" />
                      <div className="h-4 bg-muted rounded w-16 animate-pulse" />
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Content Loading */}
          <Card className="border-border">
            <CardContent className="p-8">
              <div className="space-y-4">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="h-4 bg-muted rounded w-full animate-pulse" />
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-4xl mx-auto px-8 py-8 space-y-8">
        
        {/* Header Card */}
        <Card className="border-border">
          <CardContent className="p-8">
            {/* Breadcrumb Navigation */}
            <nav className="mb-6">
              <ol className="flex items-center gap-2 text-sm">
                <li>
                  <button 
                    onClick={() => navigate("/build/compositions")}
                    className="text-muted-foreground hover:text-maitai-green transition-colors font-gc-reg"
                  >
                    Compositions
                  </button>
                </li>
                <li className="text-muted-foreground/40">
                  <span>/</span>
                </li>
                <li>
                  <button 
                    onClick={() => navigate(`/build/compositions/${id}`)}
                    className="text-muted-foreground hover:text-maitai-green transition-colors font-gc-reg"
                  >
                    {existingComposition?.name}
                  </button>
                </li>
                <li className="text-muted-foreground/40">
                  <span>/</span>
                </li>
                <li>
                  <span className="text-maitai-green font-gc-med">Edit</span>
                </li>
              </ol>
            </nav>

            {/* Page Header */}
            <div className="space-y-1">
              <h1 className="text-4xl font-gc-demi-bold text-foreground tracking-tight">
                Edit Composition
              </h1>
              <p className="text-lg text-muted-foreground font-gc-reg">
                Create a new version of your composition with updated datasets and configuration
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Progress Card */}
        <Card className="border-border">
          <CardContent className="p-8">
            <div className="space-y-6">
              {/* Progress Header */}
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-gc-med text-foreground">
                    Step {currentStep + 1} of {steps.length}
                  </h2>
                  <p className="text-sm text-muted-foreground">
                    {steps[currentStep].title}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-sm font-gc-med text-foreground">
                    {completedSteps}/{steps.length} complete
                  </div>
                  <div className="flex items-center gap-1">
                    {steps.map((_, index) => (
                      <div
                        key={index}
                        className={cn(
                          "w-2 h-2 rounded-full transition-colors",
                          index < currentStep ? "bg-maitai-green" : 
                          index === currentStep ? "bg-maitai-green/60" : "bg-muted"
                        )}
                      />
                    ))}
                  </div>
                </div>
              </div>

              {/* Step Navigation */}
              <div className="flex space-x-8">
                {steps.map((step, index) => (
                  <div
                    key={step.key}
                    className={cn(
                      "flex items-center gap-3 text-sm transition-colors",
                      index === currentStep
                        ? "text-maitai-green"
                        : index < currentStep
                        ? "text-foreground"
                        : "text-muted-foreground"
                    )}
                  >
                    <div className={cn(
                      "flex items-center justify-center w-6 h-6 rounded-full text-xs font-gc-med transition-colors",
                      index === currentStep
                        ? "bg-maitai-green text-white"
                        : index < currentStep
                        ? "bg-maitai-green text-white"
                        : "bg-muted text-muted-foreground"
                    )}>
                      {index < currentStep ? (
                        <CheckIcon className="w-3 h-3" />
                      ) : (
                        <span>{index + 1}</span>
                      )}
                    </div>
                    <span className="font-gc-reg">{step.title}</span>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Step Content Card */}
        <Card className="border-border">
          <CardContent className="p-8">
            {steps[currentStep].component}
          </CardContent>
        </Card>

        {/* Navigation Card */}
        <Card className="border-border">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                {currentStep > 0 && (
                  <Button
                    variant="outline"
                    onClick={handlePrev}
                    disabled={loading}
                    className="gap-2"
                  >
                    <ChevronLeftIcon className="w-4 h-4" />
                    Previous Step
                  </Button>
                )}
              </div>
              <div>
                {currentStep < steps.length - 1 ? (
                  <Button
                    onClick={handleNext}
                    disabled={!canProceed() || loading}
                    className="gap-2"
                  >
                    Continue
                    <ChevronRightIcon className="w-4 h-4" />
                  </Button>
                ) : (
                  <Button
                    disabled={!canProceed() || loading}
                    onClick={updateComposition}
                    className="gap-2"
                  >
                    {loading && <Loader2Icon className="w-4 h-4 animate-spin" />}
                    {loading ? "Creating new version..." : (
                      <>
                        <FileTextIcon className="w-4 h-4" />
                        Create New Version
                      </>
                    )}
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Dataset Configuration Modal */}
      <Dialog open={isConfigModalVisible} onOpenChange={setIsConfigModalVisible}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Configure Dataset Sampling</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="sampleType">Sample Type</Label>
              <Select value={configSampleType} onValueChange={(value: "request" | "session") => setConfigSampleType(value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select sample type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="request">Request</SelectItem>
                  <SelectItem value="session">Session</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="samplePercentage">Sample Percentage</Label>
              <div className="flex items-center gap-2">
                <Input
                  id="samplePercentage"
                  type="number"
                  min={1}
                  max={100}
                  value={configSamplePercentage}
                  onChange={(e) => setConfigSamplePercentage(Number(e.target.value))}
                  placeholder="Enter percentage"
                  className="flex-1"
                />
                <span className="text-sm text-muted-foreground">%</span>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsConfigModalVisible(false)}>
              Cancel
            </Button>
            <Button onClick={handleConfigModalOk}>
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CompositionEdit; 